import { action, observable } from 'mobx';

import { QueryCombiner, RouteStore, SerializedData, Store } from 'bernie-core';
import { DynamicElementsStore, PriceSummaryStore } from '.';
import { FlightsSelectedJourneyReviewType } from '/types/shared/flights-selected-journey-review';
import { FlightsJourneyAvailableFares } from '/types/shared/flights-journey-available-fares-information';
import { FlightsShoppingPwaFlightInformationResults } from '../__generated__/api';
import FlightsJourneyDetailsDynamicElements = FlightsShoppingPwaFlightInformationResults.FlightsJourneyDetailsDynamicElements;
import FareTypes = FlightsShoppingPwaFlightInformationResults.FareTypes;
import DynamicElements = FlightsShoppingPwaFlightInformationResults.DynamicElements;

export class FlightsSelectedJourneyReviewStore extends Store {
  @observable public flightsSelectedJourneyReview: FlightsSelectedJourneyReviewType[];
  @observable public detailsVisible = false;
  public selectedIdentifiers: Map<number, string>;
  @observable public selectedLegIndex = -1;
  public defaultFareIndex = 0;

  public hydrate(data: SerializedData): void {
    this.selectedLegIndex = -1;
    Object.assign(this, data);
  }

  public flightsSelectedJourneyReviewItem = (index: number): FlightsSelectedJourneyReviewType => {
    const flightsSelectedJourneyReview = this.flightsSelectedJourneyReview && this.flightsSelectedJourneyReview[index];

    return flightsSelectedJourneyReview;
  };

  public getJourneyReviewItem = (availableFaresInformation: FlightsJourneyAvailableFares) => {
    return [
      {
        flightsJourneyHeaders: availableFaresInformation.flightsJourneyHeaders,
        flightsJourneySummary: availableFaresInformation.flightsJourneySummary,
        flightsJourneyInformation: {
          details: availableFaresInformation.flightsJourneyInformation
            ? availableFaresInformation.flightsJourneyInformation.details
            : null,
          flightJourneyDetails: availableFaresInformation.flightsJourneyInformation
            ? availableFaresInformation.flightsJourneyInformation.flightJourneyDetails
            : null
        }
      }
    ];
  };

  @action
  public setFlightsSelectedJourneyReview = (journeyDetailsList: FlightsJourneyDetailsDynamicElements[]) => {
    if (journeyDetailsList) {
      this.flightsSelectedJourneyReview.map((selectedJourneyItem, selectedJourneyIndex) => {
        selectedJourneyItem.fareDetails.fareSummary.title = journeyDetailsList[selectedJourneyIndex].fareName;
        selectedJourneyItem.flightsJourneyInformation.flightJourneyDetails.journeyParts.map((item, index) => {
          item.flightsConnectionInformation.flightsConnection.cabinClassAndBookingCode =
            journeyDetailsList[selectedJourneyIndex].journeyPartsDynamicElements[index].cabinClassAndBookingCode;
        });
      });
    }
  };

  @action
  public initialiseDynamicElementsIdentifier() {
    if (this.flightsSelectedJourneyReview && this.flightsSelectedJourneyReview.length) {
      this.selectedIdentifiers = new Map();
      this.flightsSelectedJourneyReview.forEach((flightsSelectedJourneyReview, index) => {
        const identifier = this.getSelectedJourneyIdentifier(flightsSelectedJourneyReview);
        if (identifier) {
          this.selectedIdentifiers.set(index, identifier);
        }
      });
    }
  }

  @action
  private setSelectedFares = (identifier: string) => {
    if (identifier) {
      this.flightsSelectedJourneyReview.forEach(selectedJourneyItem => {
        if (this.verifyFareSummaryPresent(selectedJourneyItem)) {
          const { flightsSelectedJourneyAvailableFaresInformation } = selectedJourneyItem.fareDetails.fareSummary;
          const fareTypes = flightsSelectedJourneyAvailableFaresInformation.fareChoiceInformation.fareTypes;
          const fare = fareTypes.find(fare => fare.identifier === identifier);
          if (fare) {
            fareTypes.forEach(fare => (fare.selected = false));
            fare.selected = true;
          }
        }
      });
    }
  };

  private verifyFareSummaryPresent(flightsSelectedJourneyReview: FlightsSelectedJourneyReviewType): boolean {
    const { fareDetails } = flightsSelectedJourneyReview;
    const { fareSummary } = fareDetails || {};
    if (
      flightsSelectedJourneyReview &&
      fareDetails &&
      fareSummary &&
      fareSummary.flightsSelectedJourneyAvailableFaresInformation
    ) {
      return true;
    }

    return false;
  }

  private getSelectedJourneyIdentifier(flightsSelectedJourneyReview: FlightsSelectedJourneyReviewType): string {
    if (this.verifyFareSummaryPresent(flightsSelectedJourneyReview)) {
      return flightsSelectedJourneyReview.fareDetails.fareSummary.flightsSelectedJourneyAvailableFaresInformation
        .fareChoiceInformation.fareTypes[this.defaultFareIndex].identifier;
    }
  }

  public updateDynamicIdentifier = (index: number, identifier: string) => {
    if (this.selectedIdentifiers && this.selectedIdentifiers.get(index)) {
      this.selectedIdentifiers.set(index, identifier);
    }
  };

  public getIdentifier = () => {
    let appendedIdentifier;
    if (this.selectedIdentifiers && this.selectedIdentifiers.size) {
      this.selectedIdentifiers.forEach((identifier, index) => {
        appendedIdentifier = appendedIdentifier
          ? appendedIdentifier.concat(this.selectedIdentifiers.get(index))
          : this.selectedIdentifiers.get(index);
      });
    }

    return appendedIdentifier;
  };

  public updateInformation = (
    itemIndex: number,
    identifier: string,
    fareTypes: FareTypes[],
    dynamicElementsStore: DynamicElementsStore,
    priceSummaryStore: PriceSummaryStore,
    routeStore: RouteStore
  ) => {
    const identifierToUpdateElements = this.updatePriceSummary(
      itemIndex,
      identifier,
      fareTypes,
      dynamicElementsStore,
      priceSummaryStore
    );

    this.updateFarePrice(itemIndex, identifier, dynamicElementsStore);

    this.updateFlightDetails(identifierToUpdateElements, dynamicElementsStore);

    const queryCombiner: QueryCombiner = (prevQuery, newQuery) => newQuery;
    routeStore.updateSearch(
      routeStore.mergeQueryString(routeStore.search, { identifier: identifierToUpdateElements, pwaDialog: undefined }),
      undefined,
      true,
      queryCombiner
    );
  };

  private updateFlightDetails = (identifierToUpdateElements: string, dynamicElementsStore: DynamicElementsStore) => {
    const journeyDetailsList = dynamicElementsStore.getFlightsJourneyDetailsById(identifierToUpdateElements);
    this.setFlightsSelectedJourneyReview(journeyDetailsList);
  };

  private updatePriceSummary(
    itemIndex: number,
    identifier: string,
    fareTypes: FareTypes[],
    dynamicElementsStore: DynamicElementsStore,
    priceSummaryStore: PriceSummaryStore
  ): string {
    this.updateDynamicIdentifier(itemIndex, identifier);
    const appendedLegsIdentifier = this.getIdentifier();
    let identifierToUpdateElements;
    let priceSummary = dynamicElementsStore.getPriceSummaryById(appendedLegsIdentifier);
    if (priceSummary === null || priceSummary === undefined) {
      const identifierForSameFares = identifier.repeat(this.flightsSelectedJourneyReview?.length);
      priceSummary = dynamicElementsStore.getPriceSummaryById(identifierForSameFares);
      identifierToUpdateElements = identifierForSameFares;
    } else {
      identifierToUpdateElements = appendedLegsIdentifier;
    }
    priceSummaryStore.setPriceSummary(priceSummary);
    this.setSelectedFares(identifier);
    const selectedFareType = fareTypes ? fareTypes.find(fare => fare.selected) : null;
    selectedFareType.chooseFareAction.analyticsList.map(item => {
      priceSummary.buttonAction.analyticsList.push(item);
    });

    return identifierToUpdateElements;
  }

  private getFareCombinations(
    identifier: string,
    dynamicElementsStore: DynamicElementsStore
  ): Map<string, DynamicElements> {
    const combinationMap = new Map();
    if (dynamicElementsStore.dynamicElementsMap) {
      for (const [dynamicElementIdentifier, dynamicElements] of dynamicElementsStore.dynamicElementsMap.entries()) {
        if (dynamicElementIdentifier.includes(identifier)) {
          combinationMap.set(dynamicElementIdentifier, dynamicElements);
        }
      }
    }

    return combinationMap;
  }

  private updateFareFromCombinationMap = (fareCombinationMap: Map<string, DynamicElements>, fare: FareTypes) => {
    for (const [dynamicElementIdentifier, dynamicElements] of fareCombinationMap.entries()) {
      if (dynamicElementIdentifier.includes(fare.identifier)) {
        fare.totalPrice = dynamicElements.totalPrice;
        const farePricePerPerson = dynamicElements.fareChoiceInformationDynamicElement?.farePricePerPerson;
        if (farePricePerPerson) {
          fare.formattedPrice.completeText = farePricePerPerson;
          fare.formattedPrice.items[0].text = farePricePerPerson;
        }
      }
    }
  };

  private updateFarePrice = (itemIndex: number, identifier: string, dynamicElementsStore: DynamicElementsStore) => {
    const fareCombinationMap = this.getFareCombinations(identifier, dynamicElementsStore);
    this.flightsSelectedJourneyReview
      .filter(
        (flightsSelectedJourneyReviewItem, index) =>
          index !== itemIndex && this.verifyFareSummaryPresent(flightsSelectedJourneyReviewItem)
      )
      .forEach(flightsSelectedJourneyReviewItem => {
        flightsSelectedJourneyReviewItem.fareDetails.fareSummary.flightsSelectedJourneyAvailableFaresInformation.fareChoiceInformation.fareTypes.forEach(
          fare => {
            this.updateFareFromCombinationMap(fareCombinationMap, fare);
          }
        );
      });
  };
}

FlightsSelectedJourneyReviewStore.DEFAULTS = {
  detailsVisible: false,
  selectedLegIndex: -1,
  defaultFareIndex: 0
};
